import { API_CONFIG } from '../../services/config';
import type { Product } from '../../types/product';
import { ALL_BRANCHES } from '../../config/branches';

// La sucursal principal: sus datos viven en src/config/branches.json.
const MAIN = ALL_BRANCHES[0];

/**
 * Datos fijos del sitio. En la plantilla original venian del endpoint
 * `/elements` (Airtable); aqui viven en codigo porque nuestra API (EBM) solo
 * expone el catalogo.
 */
export const SITE = {
  logo: '/imgs/logo.webp',
  logoPng: '/imgs/logo.png',
  banner: '/imgs/banner.webp',
  customBuilds: '/imgs/custom-builds.webp',
  phones: MAIN.phones,
  email: 'spartechrd@gmail.com',
  whatsapp: API_CONFIG.WHATSAPP_NUMBER,
  instagram: 'https://www.instagram.com/spartechrd',
  facebook: 'https://www.facebook.com/profile.php?id=61552827808530',
  address: MAIN.address,
  mapsUrl: MAIN.mapsUrl,
  mapsEmbed: MAIN.mapsEmbed,
  hours: MAIN.hours,
} as const;

/** Abre WhatsApp con un mensaje ya escrito. */
export function openWhatsApp(text: string) {
  window.open(
    `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(text)}`,
    '_blank',
    'noopener',
  );
}

const rd = new Intl.NumberFormat('es-DO', {
  style: 'currency',
  currency: 'DOP',
  currencyDisplay: 'code',
  minimumFractionDigits: 2,
});

/** RD$1,234.00 — el mismo formato que usaba la plantilla (`currency:'RD$'`). */
export function formatRD(value: number): string {
  return rd.format(value).replace(/DOP\s?/, 'RD$');
}

export function productUrl(product: Pick<Product, 'id'>): string {
  return `/products/${API_CONFIG.COMPANY_ID}/${product.id}`;
}

/**
 * Precio a mostrar y precio tachado. En EBM `price` ya trae la oferta aplicada
 * y `listPrice` es el original; sin oferta se usa el menor precio por sucursal.
 */
export function productPricing(product: Product): { price: number; regular: number | null } {
  const onSale =
    !!product.isOnSale && product.listPrice != null && product.listPrice > (product.price ?? 0);
  if (onSale) return { price: product.price ?? 0, regular: product.listPrice ?? null };

  const branchPrices = product.availability.map((a) => a.price).filter((p) => p > 0);
  const min = branchPrices.length > 0 ? Math.min(...branchPrices) : 0;
  return { price: min > 0 ? min : product.price ?? 0, regular: null };
}

/**
 * Convierte texto plano del catalogo en HTML simple, como el pipe `richtext`
 * de la plantilla: **negritas**, lineas con "- " como lista y saltos de linea.
 * Escapa el HTML de origen antes de transformar.
 */
export function richText(value: string): string {
  const escaped = value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const lines = escaped.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  let html = '';
  let inList = false;
  for (const line of lines) {
    const bullet = /^[-•*]\s+/.test(line);
    const content = line.replace(/^[-•*]\s+/, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    if (bullet && !inList) {
      html += '<ul>';
      inList = true;
    } else if (!bullet && inList) {
      html += '</ul>';
      inList = false;
    }
    html += bullet ? `<li>${content}</li>` : `<p>${content}</p>`;
  }
  if (inList) html += '</ul>';
  return html;
}
